// Single fact module for the site. Pages read facts from here, never retype them.
// Every entry names its `source` (a key of SOURCES). Source precedence when two
// disagree: BRIEF > TICKET (CV > ETIS > press) > prototypes. See plans/WEB-1/.
// public/facts.json mirrors this file without the `source` fields
// (tests/e2e/facts.spec.js keeps them equal): update both together.

export const SOURCES = {
  cv: 'CV-M.Tamm.pdf (Martin, October 2026), as recorded in plans/WEB-1/TICKET.md',
  etis: 'ETIS research CV, https://www.etis.ee/CV/Martin_Tamm3/eng (checked 2026-10-15)',
  brief: 'plans/WEB-1/BRIEF.md decisions (Martin, 2026-10-15)',
  plan: 'plans/WEB-1/PLAN.md answers (Martin, 2026-10-15)',
  doi: 'Springer record via Crossref, https://doi.org/10.1007/978-3-032-11976-6_23',
  forteInterview:
    'Forte (Delfi), 30 Jul 2026, https://forte.delfi.ee/artikkel/120600576/tunne-teadlast-martin-tamm-tehisaru-mis-ei-naita-oma-allikaid-on-lihtsalt-enesekindel-vestluskaaslane',
  forteOpinion:
    'Forte (Delfi), 4 Aug 2026, https://forte.delfi.ee/artikkel/120601525/repliik-martin-tamme-ai-ei-paranda-katkiseid-tooprotsesse-vaid-kinnistab-segaduse',
  trialoog:
    'TalTech Trialoog, 4 Aug 2026, https://trialoog.taltech.ee/treeningplatvorm-kulg-uhendab-jooksurajal-teaduse-ja-tehisaru/',
  ridangoCustomers:
    'The Baltic Times, 1 Jun 2023, https://www.baltictimes.com/estonian_company_organizes_public_transportation_all_over_the_world__from_lithuania_to_singapore_and_the_middle_east/ (Ridango customers: Tallinn Transport Department, Pärnu Public Transport Centre)',
  workstationBrief: 'plans/WEB-6/BRIEF.md decisions (Martin, 2026-10-17)',
}

export const identity = {
  name: 'Martin Tamm',
  type: 'Person',
  headline: 'Doctoral researcher at TalTech · Head of Product & AI at KULG',
  website: 'https://mtamm.eu',
  analystSince: 2016,
  sameAs: ['https://www.linkedin.com/in/mtamm', 'https://www.etis.ee/CV/Martin_Tamm3/eng'],
  source: 'plan',
}

export const contact = {
  email: 'martin@mtamm.eu',
  linkedin: 'https://www.linkedin.com/in/mtamm',
  etis: 'https://www.etis.ee/CV/Martin_Tamm3/eng',
  location: 'Tartu / Tallinn, available EU-wide',
  replyTime: 'within one working day',
  primaryCta: { label: 'Request a 30-minute call', cost: 'free', path: '/contact' },
  source: 'brief',
}

export const services = [
  { name: 'Agent systems', path: '/services/agent-systems', source: 'brief' },
  { name: 'Research pipelines', path: '/services/research-pipelines', source: 'brief' },
  { name: 'Spec-first agentic coding', path: '/services/agentic-coding', source: 'brief' },
]

// start/end as YYYY-MM; end null = current.
export const career = [
  { employer: 'KULG', role: 'Head of Product & AI', start: '2026-03', end: null, source: 'cv' },
  { employer: 'TalTech', role: 'Doctoral researcher, junior research fellow', start: '2025-02', end: null, source: 'cv' },
  { employer: 'Cybernetica', role: 'Senior analyst, customs clearance (CCI)', start: '2023-05', end: '2026-09', source: 'cv' },
  { employer: 'Piletilevi', role: 'Data analyst', start: '2022-12', end: '2024-02', source: 'cv' },
  { employer: 'Ridango', role: 'Project business analyst', start: '2021-05', end: '2023-05', source: 'cv' },
  { employer: 'Icefire', role: 'Business system analyst', start: '2019-06', end: '2021-05', source: 'cv' },
  { employer: 'Finestmedia', role: 'IT system analyst, lead IT system analyst from 2018', start: '2016-10', end: '2020-06', source: 'cv' },
  { employer: 'Ericsson', role: 'MES engineer, team lead', start: '2013-05', end: '2016-10', source: 'cv' },
]

// year = year completed; inProgress entries give the start instead.
export const education = [
  { degree: 'Doctoral studies', field: 'Information technology', institution: 'TalTech', start: '2025-01', inProgress: true, source: 'cv' },
  { degree: 'MSc', field: 'Business information technology', institution: 'TalTech', year: 2026, source: 'cv' },
  { degree: 'MSc', field: 'Product development and production technologies', institution: 'TalTech', year: 2016, source: 'cv' },
  { degree: 'BASc', institution: 'Tallinn University of Applied Sciences', year: 2014, source: 'cv' },
]

export const researchProjects = [
  { code: 'VEU24027', name: 'CIRPASS-2', start: 2025, end: 2027, role: 'junior research fellow', load: 0.9, source: 'etis' },
  { code: '101181704', name: 'Textile EPR', start: 2025, end: 2028, role: 'junior research fellow', load: 0.9, source: 'etis' },
  { code: 'TEM-TA313', name: 'DPP semantic and conformance intelligence', start: 2026, end: 2029, role: 'junior research fellow', load: 0.9, source: 'etis' },
  { code: 'TEM-TA331', name: 'Building life-cycle carbon data', start: 2026, end: 2029, role: 'junior research fellow', load: 0.9, source: 'etis' },
]

export const publications = [
  {
    title:
      'Integrating Digital Product Passports in E-Commerce: An Architectural Framework for Sustainability and AI-Driven Value',
    authors: ['Martin Tamm', 'Dirk Draheim', 'Tanel Tammet', 'Ingrid Pappel'],
    venue: 'Information Integration and Web Intelligence, iiWAS 2025, Matsue, Japan',
    publisher: 'Springer',
    series: 'Lecture Notes in Computer Science 16330',
    pages: '315–325',
    year: 2025,
    doi: '10.1007/978-3-032-11976-6_23',
    url: 'https://doi.org/10.1007/978-3-032-11976-6_23',
    source: 'doi',
  },
]

// Titles are English translations of Estonian articles.
export const press = [
  {
    date: '2026-08-04',
    outlet: 'TalTech Trialoog',
    kind: 'Feature',
    language: 'et',
    title: 'Training platform KULG brings science and AI to the running track',
    url: 'https://trialoog.taltech.ee/treeningplatvorm-kulg-uhendab-jooksurajal-teaduse-ja-tehisaru/',
    source: 'trialoog',
  },
  {
    date: '2026-08-04',
    outlet: 'Forte (Delfi)',
    kind: 'Opinion',
    language: 'et',
    title: 'AI does not fix broken work processes, it locks the mess in',
    url: 'https://forte.delfi.ee/artikkel/120601525/repliik-martin-tamme-ai-ei-paranda-katkiseid-tooprotsesse-vaid-kinnistab-segaduse',
    source: 'forteOpinion',
  },
  {
    date: '2026-07-30',
    outlet: 'Forte (Delfi)',
    kind: 'Interview',
    language: 'et',
    title: 'Meet the scientist: AI that does not show its sources is just a confident conversation partner',
    url: 'https://forte.delfi.ee/artikkel/120600576/tunne-teadlast-martin-tamm-tehisaru-mis-ei-naita-oma-allikaid-on-lihtsalt-enesekindel-vestluskaaslane',
    source: 'forteInterview',
  },
]

// Approved list (BRIEF Q18–Q20): projects done as an employee. Text only, no logos,
// no results. Shown as "<client>: <project>, as <role> at <employer>".
// Tallinn and Pärnu: Martin worked on them at Ridango (confirmed 2026-10-15);
// official English names per SOURCES.ridangoCustomers.
export const selectedProjects = [
  { client: 'Estonian Tax and Customs Board (EMTA)', project: 'Impulss', role: 'senior analyst', employer: 'Cybernetica', source: 'brief' },
  { client: 'Estonian Tax and Customs Board (EMTA)', project: 'Centralised Clearance for Import (CCI)', role: 'senior analyst', employer: 'Cybernetica', source: 'brief' },
  { client: 'STAR', project: 'the social services and benefits register', role: 'senior analyst', employer: 'Cybernetica', source: 'brief' },
  { client: 'Tallinn Transport Department', project: 'public transport', role: 'project business analyst', employer: 'Ridango', source: 'brief' },
  { client: 'Pärnu Public Transport Centre', project: 'public transport', role: 'project business analyst', employer: 'Ridango', source: 'brief' },
  { client: 'Skånetrafiken (Sweden)', project: 'public transport', role: 'project business analyst', employer: 'Ridango', source: 'brief' },
  { client: 'Region Västmanland (Sweden)', project: 'public transport', role: 'project business analyst', employer: 'Ridango', source: 'brief' },
  { client: 'Helsinki', project: 'the Tallinn–Tartu–Helsinki joint ticket', role: 'project business analyst', employer: 'Ridango', source: 'brief' },
  { client: 'Luminor', project: 'bank business data model', role: 'business system analyst', employer: 'Icefire', source: 'brief' },
  { client: 'Social Insurance Board', project: 'SKAIS2 architecture', role: 'business system analyst', employer: 'Icefire', source: 'brief' },
  { client: 'Elering', project: 'X-Road biofuel integration analysis', role: 'business system analyst', employer: 'Icefire', source: 'brief' },
  { client: 'Hansapost, Bauhof, Ordi', project: 'e-commerce analysis (PIM/ERP integrations)', role: 'lead IT system analyst', employer: 'Finestmedia', source: 'brief' },
]

// Personal AI workstation (WEB-6). The only number the Workstation page may show.
export const workstation = {
  startingPoint: 'a mini-PC with 128 GB of RAM',
  smallerRamTested: false,
  ownUse: 'work in progress',
  source: 'workstationBrief',
}

export const profile = {
  identity,
  contact,
  services,
  career,
  education,
  researchProjects,
  publications,
  press,
  selectedProjects,
  workstation,
}

export default profile
